import {
  Contact,
  NotificationChannel,
  configDisplayValue,
  contactDisplayLabel,
} from '@/core/notifications/model';

function configsEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => a[key] === b[key]);
}

// Delivery overrides must point to a config the user already verified, so this
// renders a select over the verified contacts of the channel instead of free text.
// A stored override that is no longer a verified contact is kept as an extra
// option so editing does not silently drop it.
export default function ContactOverrideSelect({
  channel,
  contacts,
  value,
  onChange,
  size,
}: {
  channel: NotificationChannel;
  contacts: Contact[];
  value: Record<string, unknown> | undefined;
  onChange: (config: Record<string, unknown>) => void;
  size?: 'sm';
}) {
  const verified = contacts.filter(
    contact => contact.channelKey === channel.key && contact.isVerified,
  );
  const hasValue = !!value && Object.keys(value).length > 0;
  const matchedContact = hasValue
    ? verified.find(contact => configsEqual(contact.config, value!))
    : undefined;
  const isStaleValue = hasValue && !matchedContact;

  const STALE = '__stale__';

  const handleChange = (contactId: string) => {
    const contact = verified.find(c => c.id === contactId);
    onChange(contact ? contact.config : {});
  };

  return (
    <select
      className={`form-select ${size === 'sm' ? 'form-select-sm' : ''}`}
      value={matchedContact?.id ?? (isStaleValue ? STALE : '')}
      onChange={event => handleChange(event.target.value)}
    >
      <option value="">Default contact</option>
      {verified.map(contact => (
        <option key={contact.id} value={contact.id}>
          {contactDisplayLabel(contact)}
          {contact.isDefault ? ' (default)' : ''}
        </option>
      ))}
      {isStaleValue && (
        <option value={STALE}>{configDisplayValue(channel, value!)} (no longer verified)</option>
      )}
    </select>
  );
}
